import React from "react";
import { useQuery } from "react-query";

import { useEnv } from "../env/provider";
import { useApi } from "../network/provider";

import { parseRouteMap, RouteMap } from "./route-map";

export const ROUTE_MAP_QUERY_KEY = "route-map";

/** The appconfig feature toggle whose payload carries the ownership map. */
const TOGGLE_NAME = "family_web_v2_routes";

type PublicToggleResponse = { enabled: boolean; payload: unknown };

export type RouteMapResult = {
  /** False until the request has settled, either way. Nothing may act on the
   *  map — not even to conclude there is none — before it flips. */
  resolved: boolean;
  enabled: boolean;
  map: RouteMap | null;
};

/**
 * The ownership map, fetched once per page load. Fails closed: while the
 * request is in flight, when the toggle is off, and on any error the result
 * carries no map, so nothing is forwarded. That is the safe default — a
 * forward is a full page load the user cannot undo, whereas not forwarding
 * only means they see the screen this app already serves.
 */
export function useRouteMap(): RouteMapResult {
  const api = useApi();
  const { appconfig } = useEnv().services;

  const { data, status } = useQuery(
    [ROUTE_MAP_QUERY_KEY],
    () =>
      api.get<PublicToggleResponse>(
        `/v1/feature-toggle/public/${TOGGLE_NAME}`,
        { baseUrl: appconfig },
      ),
    // The map cannot change mid-session: a change ships as an appconfig
    // deploy, and re-reading it after a screen is already rendered would
    // teleport the user out of it.
    { staleTime: Infinity, cacheTime: Infinity, retry: false },
  );

  // Stable across renders so the gate's effect only fires when the answer
  // actually changes; react-query keeps `data` referentially stable itself.
  return React.useMemo(() => {
    const resolved = status !== "loading";
    const map = data?.enabled ? parseRouteMap(data.payload) : null;

    return map
      ? { resolved, enabled: true, map }
      : { resolved, enabled: false, map: null };
  }, [data, status]);
}
